"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import Icon from "../../components/Icon";
import { ApiError, api } from "../../lib/api";
import { conNext, destinoSeguro } from "../ingresar/destino";

function Recuperar() {
  const params = useSearchParams();
  // Se arrastra el destino: si llegó acá desde /ingresar?next=/tecnico/abc,
  // el botón de volver tiene que devolverlo al mismo lugar.
  const next = destinoSeguro(params.get("next"), "");
  const [identifier, setIdentifier] = useState("");
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState("");
  const [espera, setEspera] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setEspera(false);
    setBusy(true);
    try {
      await api("/auth/forgot", { method: "POST", body: JSON.stringify({ identifier }) });
      setEnviado(true);
    } catch (e) {
      // El 429 viene con su propio texto en español desde el API
      setEspera(e instanceof ApiError && e.status === 429);
      setError(e instanceof Error ? e.message : "No se pudo enviar");
    } finally {
      setBusy(false);
    }
  }

  const input =
    "w-full rounded-xl bg-[#101516] border border-white/15 px-4 py-3 text-[15px] placeholder-[#808D88] focus:border-[#E8A23C]";

  return (
    <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] flex items-center justify-center p-6">
      {enviado ? (
        <div className="w-full max-w-sm text-center">
          <span className="w-14 h-14 rounded-2xl bg-[#5CBE8F]/12 text-[#5CBE8F] grid place-items-center mx-auto">
            <Icon name="mail" size={26} />
          </span>
          <h1 className="mt-4 text-2xl font-bold tracking-tight">Revisá tu correo</h1>
          <p className="mt-3 text-[#B5C0BB] text-[15px] leading-relaxed">
            Si esa cuenta existe y tiene un email cargado, te mandamos un enlace para cambiar la
            contraseña. Vale por una hora.
          </p>
          <Link href={conNext("/ingresar", next)} className="mt-6 inline-block text-[#E8A23C] font-semibold">
            Volver a ingresar
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="w-full max-w-sm flex flex-col gap-5">
          <div>
            <p className="font-mono text-xs tracking-[0.2em] text-[#E8A23C] uppercase mb-2">
              Busco · recuperar acceso
            </p>
            <h1 className="text-2xl font-bold tracking-tight">¿Olvidaste tu contraseña?</h1>
            <p className="mt-2 text-sm text-[#B5C0BB]">
              Poné tu teléfono o email y te mandamos un enlace para cambiarla.
            </p>
          </div>
          <div>
            <label htmlFor="identificador" className="block text-sm text-[#B5C0BB] mb-1.5">
              Teléfono o email
            </label>
            <input
              id="identificador"
              name="identificador"
              className={input}
              placeholder="09xxxxxxxx o tu@email.com"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="send"
              required
              autoFocus
            />
          </div>
          {error && (
            <p
              role="alert"
              className={`text-sm rounded-lg px-4 py-2.5 flex items-start gap-2 ${
                espera ? "text-[#E8A23C] bg-[#E8A23C]/10" : "text-[#DE705C] bg-[#DE705C]/10"
              }`}
            >
              <Icon name={espera ? "clock" : "alert"} size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </p>
          )}
          <button disabled={busy}
            className="rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3.5 hover:bg-[#F2B054] active:bg-[#D8912C] transition disabled:opacity-50">
            {busy ? "Enviando…" : "Enviarme el enlace"}
          </button>
          <Link href={conNext("/ingresar", next)} className="text-sm text-center text-[#808D88] hover:text-[#E8A23C]">
            Volver
          </Link>
        </form>
      )}
    </main>
  );
}

// useSearchParams necesita un límite de Suspense para que la página siga siendo
// prerenderizable; el fallback es el fondo pelado para que no parpadee en blanco.
export default function RecuperarPage() {
  return (
    <Suspense fallback={<main className="min-h-screen bg-[#14191B]" />}>
      <Recuperar />
    </Suspense>
  );
}
